// @flow
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import FLAVOURS from "flavours/flavours.js";
import { Environment } from "Commons/Utilities";
import { setShowCookieSettings } from "redux/actions/ui";
import { triggerFocusConsentsSettings } from "Commons/Utilities/GoogleTagManager";
import { TrustPilotWidget, useTrustPilot } from "Commons/Components/TrustPilotComponents/TrustPilotComponents";
import {
  FragenBaseRoute,
  InternalDataProtectionRoute,
  InternalImpressumRoute,
  PresseBaseRoute,
  UnternehmenBaseRoute,
  UnternehmenJobsBaseRoute,
  ContactRoute,
} from "modules/ContentPages/misc/ContentPageRoutes";
import { ElectricityRoute, GasRoute } from "modules/ChangeService/ChangeServices/Routes";
import { SecondLandingPageRoute } from "modules/DynamicLandingPage/misc/Routes";
import { scrollToPosition } from "Commons/Utilities/scrollTo";
import type { LogoT } from "flavours/flavours";

import Styles from "./Footer.module.scss";

type Props = {
  logo: LogoT,
};

const Footer = ({ logo }: Props) => {
  const { trustPilotRef } = useTrustPilot();
  const { t } = useTranslation("footer");
  const isFocusApp = FLAVOURS[Environment.get(Environment.CLIENT_ID)].name === FLAVOURS.focusApp.name;
  const isCookieBannerActive = useSelector(({ ui: { cookieBannerStatus } }) => cookieBannerStatus);
  const dispatch = useDispatch();

  return (
    <div className={Styles.footer} id="footerNavigation">
      <div className={Styles.topSection}>
        <div className={Styles.logoPlaceHolder}>
          <img src={logo.main} className={Styles.logo} alt="remind.me" />
          <div className={Styles.logoTrustPilot}>
            <TrustPilotWidget
              templateId="53aa8807dec7e10d38f59f32"
              trustPilotRef={trustPilotRef}
              theme="light"
              height="110px"
            />
          </div>
        </div>
        <div className={Styles.lists}>
          <div className={Styles.listGroup}>
            <ul>
              <li>
                <Link
                  to={UnternehmenBaseRoute}
                  onClick={() => scrollToPosition(0, 0)}
                  data-cy="footer-unternehmen-link"
                >
                  {t("about")}
                </Link>
              </li>
              <li>
                <Link to={FragenBaseRoute} onClick={() => scrollToPosition(0, 0)} data-cy="footer-faq-link">
                  {t("faq")}
                </Link>
              </li>
              <li>
                <Link to={ContactRoute} onClick={() => scrollToPosition(0, 0)} data-cy="footer-kontakt-link">
                  {t("contact")}
                </Link>
              </li>
            </ul>
          </div>
          <div className={Styles.listGroup}>
            <ul>
              <li>
                <Link
                  to={InternalImpressumRoute}
                  onClick={() => scrollToPosition(0, 0)}
                  data-cy="footer-impressum-link"
                >
                  {t("impressum")}
                </Link>
              </li>
              <li>
                <Link
                  to={InternalDataProtectionRoute}
                  onClick={() => scrollToPosition(0, 0)}
                  data-cy="footer-data-link"
                >
                  {t("dataProtection")}
                </Link>
              </li>
              <li>
                <Link to={PresseBaseRoute} onClick={() => scrollToPosition(0, 0)} data-cy="footer-presse-link">
                  {t("press")}
                </Link>
              </li>
            </ul>
          </div>
          <div className={Styles.listGroup}>
            <ul>
              <li>
                <Link to={UnternehmenJobsBaseRoute} onClick={() => scrollToPosition(0, 0)} data-cy="footer-jobs-link">
                  {t("jobs")}
                </Link>
              </li>
              {!isCookieBannerActive && (
                <li className={Styles.clickable} onClick={() => dispatch(setShowCookieSettings(true))}>
                  {t("cookieSettings")}
                </li>
              )}
              {!isCookieBannerActive && isFocusApp && (
                <li className={Styles.clickable} onClick={() => triggerFocusConsentsSettings()}>
                  {t("cookieSettingsFocus")}
                </li>
              )}
            </ul>
          </div>
        </div>
        <div className={Styles.placeHolder}>
          <TrustPilotWidget
            templateId="53aa8807dec7e10d38f59f32"
            trustPilotRef={trustPilotRef}
            theme="light"
            height="110px"
          />
        </div>
      </div>
      <div className={Styles.bottomSection}>
        <div className={Styles.placeHolder} />
        <div className={Styles.lists}>
          <div className={Styles.listGroup}>
            <ul>
              <li className={Styles.groupName}>{t("categories:ELECTRICITY")}</li>
              <li>
                <Link
                  to={`${SecondLandingPageRoute}?categoryType=ELECTRICITY`}
                  onClick={() => scrollToPosition(0, 0)}
                  data-cy="footer-cheap-electricity-link"
                >
                  {t("cheapElectricity")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${ElectricityRoute}/anschluss/erstellen`}
                  onClick={() => scrollToPosition(0, 0)}
                  data-cy="footer-change-electricity-provider-link"
                >
                  {t("ChangeElectricityProvider")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${SecondLandingPageRoute}?categoryType=ELECTRICITY`}
                  onClick={() => scrollToPosition(0, 0)}
                  data-cy="footer-cheap-electricity-link"
                >
                  {t("CompareElectricityPrice")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${ElectricityRoute}/anschluss/erstellen?objectType=company`}
                  onClick={() => scrollToPosition(0, 0)}
                  data-cy="footer-commercial-electricity-link"
                >
                  {t("CommercialElectricity")}
                </Link>
              </li>
            </ul>
          </div>
          <div className={Styles.listGroup}>
            <ul>
              <li className={Styles.groupName}>{t("categories:GAS")}</li>
              <li>
                <Link
                  to={`${SecondLandingPageRoute}?categoryType=GAS`}
                  onClick={() => scrollToPosition(0, 0)}
                  data-cy="footer-cheap-gas-link"
                >
                  {t("cheapGas")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${GasRoute}/anschluss/erstellen`}
                  onClick={() => scrollToPosition(0, 0)}
                  data-cy="footer-change-gas-provider-link"
                >
                  {t("ChangeGasProvider")}
                </Link>
              </li>
              <li>
                <Link
                  to={`${GasRoute}/anschluss/erstellen?objectType=company`}
                  onClick={() => scrollToPosition(0, 0)}
                  data-cy="footer-commercial-gas-link"
                >
                  {t("CommercialGas")}
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className={Styles.placeHolder} />
      </div>
      <div className={Styles.signature}>
        <span>{t("remindmeGmbh", { year: new Date().getFullYear() })}</span>
      </div>
    </div>
  );
};

export default Footer;
